'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { getSourcePath } from '@/rewrites';
import Link from 'next/link';
import { Procurement } from '@/actions/dtos/tender/procurement';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import { NutsCodeDto } from '@/actions/dtos/nutsCode';
import { CpvCodeDto } from '@/actions/dtos/cpvCode';

interface SimilarTendersByCategorySectionProps {
  procurementDetails: Procurement;
}

type Code = NutsCodeDto | CpvCodeDto;

const SimilarTendersByCategorySection: React.FC<SimilarTendersByCategorySectionProps> = ({ procurementDetails }) => {
  const { cpvCodes, executionSite } = procurementDetails;
  const nutsCode = executionSite?.nutsCode;
  const [topLevelNutsCode, setTopLevelNutsCode] = useState<NutsCodeDto | null>(null);
  const [topLevelCpvCodes, setTopLevelCpvCodes] = useState<CpvCodeDto[]>([]);

  const getTopLevelCode = useCallback((code: Code): Code => {
    if (code.parent) {
      return getTopLevelCode(code.parent);
    }
    return code;
  }, []);

  useEffect(() => {
    if (nutsCode) {
      setTopLevelNutsCode(getTopLevelCode(nutsCode) as NutsCodeDto);
    }
  }, [nutsCode, getTopLevelCode]);

  useEffect(() => {
    const topLevelCpvCodes: CpvCodeDto[] = [];
    if (cpvCodes) {
      for (const cpvCode of cpvCodes) {
        if (cpvCode) {
          const topLevelCpvCode = getTopLevelCode(cpvCode) as CpvCodeDto;
          if (!topLevelCpvCodes.some((code) => code.identifier === topLevelCpvCode.identifier)) {
            topLevelCpvCodes.push(topLevelCpvCode);
          }
        }
      }
      setTopLevelCpvCodes(topLevelCpvCodes);
    }
  }, [cpvCodes, getTopLevelCode]);

  return (
    <RoundedSectionCard>
      <div className="flex w-full gap-4 max-sm:flex-col">
        <p className="min-w-fit text-base font-normal">Weitere Ausschreibungen zu: &nbsp; </p>
        <div className="flex flex-wrap gap-2">
          {topLevelCpvCodes?.map((cpvCode, index) => (
            <Link
              key={cpvCode.identifier}
              href={`${getSourcePath('/tenders')}/cpv/${cpvCode.identifier}/${cpvCode.name.replace(/\s+/g, '-')}`}
              className="font-medium"
            >
              {cpvCode.name} {index < topLevelCpvCodes.length - 1 && ' | '}
            </Link>
          ))}
          {topLevelNutsCode && ' | '}
          {topLevelNutsCode && (
            <Link
              href={`${getSourcePath('/tenders')}/nuts/${topLevelNutsCode.identifier}/${topLevelNutsCode.name.replace(/\s+/g, '-')}`}
              className="font-medium"
            >
              {topLevelNutsCode.name}
            </Link>
          )}
        </div>
      </div>
    </RoundedSectionCard>
  );
};

export default SimilarTendersByCategorySection;
